import { LoaderCircle } from 'lucide-react';

import './feature-skeleton.css';

export interface FeatureSkeletonProps {
  /** Root class of the screen being loaded, so its own page geometry applies. */
  screenClassName: string;
  label: string;
  as?: 'section' | 'main';
  /** Legacy layout hints retained while callers migrate to the unified loader. */
  back?: string;
  masthead?: string;
  leading?: boolean;
  eyebrow?: boolean;
  lead?: boolean;
  aside?: boolean;
  nav?: number;
  panels?: number;
  rows?: number;
  split?: boolean;
}

export interface FeatureLoadingProps {
  label: string;
  className?: string;
  compact?: boolean;
}

/**
 * One loading treatment for every feature and panel. It deliberately renders no
 * guessed content geometry, so stale skeleton shapes cannot flash before the
 * authoritative screen is ready.
 */
export function FeatureLoading({ label, className = '', compact = false }: FeatureLoadingProps) {
  return (
    <div
      className={`feature-loading${compact ? ' feature-loading--compact' : ''}${className ? ` ${className}` : ''}`}
      role="status"
      aria-live="polite"
      aria-label={label}
      aria-busy="true"
    >
      <LoaderCircle className="feature-loading__spinner" size={20} aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

/** Embedded cards use the same indicator instead of animated placeholder rows. */
export function SkeletonRows({
  count,
  height = 14,
}: {
  count: number;
  height?: number;
}) {
  void count;
  void height;
  return <FeatureLoading label="불러오는 중" compact />;
}

/**
 * Compatibility boundary for existing lazy routes. Despite the historical name,
 * it now renders the canonical centered loading state, not a skeleton.
 */
export function FeatureSkeleton({ screenClassName, label, as = 'section' }: FeatureSkeletonProps) {
  const Root = as;
  return (
    <Root className={`${screenClassName} feature-skeleton`} aria-label={label} aria-busy="true">
      <FeatureLoading label={label} />
    </Root>
  );
}
